import { messages } from 'constants/webphoneMessages';
import { NavigateOptions, To } from 'react-router-dom';
import { AxiosError } from 'axios';
import voipService from './voip.service';
import { states } from 'constants/webphoneStates';

class ZenviaScriptService {
  /**
   * Start zenvia script
   */
  async initZenviaScript(
    webphoneToken: string,
    navigate: (to: To, options?: NavigateOptions) => void,
    onStopCall: () => void,
    onTalkingCall: () => void,
    onConnected: () => void,
    onError: (error?: unknown) => void,
  ): Promise<void> {
    try {
      const data = await voipService.getZenviaScript(webphoneToken);

      const existingScript = document.getElementById('webphone-zenvia-script');

      if (!existingScript) {
        const script = document.createRange().createContextualFragment(data.script);
        document.body.appendChild(script);
      }

      this.startOnMessage(webphoneToken, onStopCall, onTalkingCall, onConnected, onError);
    } catch (error) {
      const { response } = error as AxiosError;
      navigate('/error', {
        state: {
          status: response?.status,
        },
      });
      onError(error);
    }
  }

  /**
   * Start event capture for zenvia script
   */
  startOnMessage(
    webphoneToken: string,
    handleStopCall: () => void,
    handleTalkingCall: () => void,
    handleScriptConnected: () => void,
    handleScriptError: () => void,
  ): void {
    window.onmessage = (event: MessageEvent<{ message: string; status: string; chamada_id: string }>): void => {
      if (event.data.message == messages.STATUS) {
        if (event.data.status == states.CLOSED) {
          handleStopCall();
        }

        if (event.data.status == states.TALKING) {
          handleTalkingCall();
        }

        if (event.data.status == states.CONNECTED) {
          handleScriptConnected();
        }
      }

      if (event.data.message == messages.CALL_ID) {
        this.IdCall(webphoneToken, parseInt(event.data.chamada_id));
      }

      if (event.data.message == messages.STATUS_ERROR) {
        handleScriptError();
      }
    };
  }

  /**
   * create a new document from getting the call id
   */
  IdCall(webphoneToken: string, idCallZenvia: number): void {
    if (webphoneToken) {
      const tag = new URLSearchParams(location.search).get('tag');
      voipService.createCalls(webphoneToken, idCallZenvia, tag);
    }
  }

  /**
   * Post'chamaNumero' message to zenvia script
   * @param numberToCall the number to call
   */
  startCall(webphoneToken: string, numberToCall: number): Promise<void> {
    const { contentWindow }: HTMLIFrameElement = document.getElementById('webphone') as HTMLIFrameElement;

    return voipService.validateToken(webphoneToken || '').then(() => {
      if (contentWindow) {
        contentWindow.postMessage(
          {
            message: 'chamaNumero',
            numero: numberToCall,
          },
          '*',
        );
      }
    });
  }

  /**
   * Post'hangup' message to zenvia script
   */
  stopCall(): void {
    const { contentWindow } = document.getElementById('webphone') as HTMLIFrameElement;

    if (contentWindow) {
      contentWindow.postMessage(
        {
          message: 'hangup',
        },
        '*',
      );
    }
  }

  /**
   * Post 'mute' message to zenvia script
   */
  mute(): void {
    const { contentWindow } = document.getElementById('webphone') as HTMLIFrameElement;

    if (contentWindow) {
      contentWindow.postMessage(
        {
          message: 'mute',
        },
        '*',
      );
    }
  }

  /**
   * Post 'dtmf' message to zenvia script
   * @param value the ura value
   */
  ura(value: number): void {
    const { contentWindow } = document.getElementById('webphone') as HTMLIFrameElement;

    if (contentWindow) {
      contentWindow.postMessage(
        {
          message: 'enviaDTMF',
          dtmf: value,
        },
        '*',
      );
    }
  }
}

export default new ZenviaScriptService();
